import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { Base, getBaseOrientations, getBaseTypes } from "../../../../../http/border/types/bases";
import { useBasesApi } from "../../../../../http/border/useBases";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateBaseModalDialogButton from "./CreateBaseModalDialogButton";
import EditBase from "./EditBase";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const BasesList: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "bases" });
  const aborter = useRef(new AbortController());
  const basesApi = useBasesApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "bases");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await basesApi.updateAdminBaseVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await basesApi.updateAdminBaseOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const baseTypes = getBaseTypes(i18n.translation);
  const baseOrientations = getBaseOrientations(i18n.translation);

  const [itemToEdit, setItemToEdit] = useState<Base>();
  const [itemToDelete, setItemToDelete] = useState<Base>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("border.base");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = basesApi.useAdminBases(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.bases.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.bases.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateBaseModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<Base>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.isVertical}</th>
            <th align="left">{i18n.translation.common.distance}</th>
            <th align="left">{i18n.translation.common.width}</th>
            <th align="left">{i18n.translation.common.height}</th>
            <th align="left">{i18n.translation.common.type}</th>
            <th align="left">{i18n.translation.common.baseOrientation}</th>
            <th align="left">{i18n.translation.common.corrugationSpan}</th>
            <th align="left">{i18n.translation.waterproofKits.singular}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={``}>
                {item.item.code}
              </Link>
            </td>
            <td>{formatBoolean(i18n.translation, item.isVertical)}</td>
            <td>{item.distance}</td>
            <td>{item.width}</td>
            <td>{item.height}</td>
            <td>{baseTypes.find((x) => x.value == item.type)?.label}</td>
            <td>{baseOrientations.find((x) => x.value == item.baseOrientation)?.label}</td>
            <td>{item.corrugationSpan && `${item.corrugationSpan} mm`} </td>
            <td>{item.waterproofKit?.item.code}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item.item),
          getDeleteAction(i18n, () => setItemToDelete(item)),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.bases.edit}>
          <EditBase onClose={() => setItemToEdit(undefined)} baseId={itemToEdit.item.id} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.bases.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                basesApi
                  .deleteAdminBase(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.bases.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default BasesList;
